import React, { useMemo, useState } from "react";
import Select from "react-select";

type Audience =
  | "ENDUSER"
  | "BROKERAGE"
  | "SIGNAGE"
  | "BROKERAGE_GROUP"
  | "SIGNAGE_GROUP";

type TriggerRule = {
  id: string;
  label: string;
  category: "SALES" | "INSTALLATION" | "REMOVAL" | "SHIPPING" | "PAYMENT";
  sendEmail: boolean;
  audience: Audience[];
  note: string;
};

const SALES_STATUS_CODES: Array<{ code: number; label: string }> = [
  { code: 100, label: "Draft" },
  { code: 101, label: "Confirmed" },
  { code: 300, label: "Shipping" },
  { code: 301, label: "Shipped" },
  { code: 302, label: "Delivered" },
  { code: 400, label: "Cancelled" },
  { code: 401, label: "Completed" },
];

const INSTALLATION_STATUS_CODES: Array<{ code: number; label: string }> = [
  { code: 100, label: "Draft" },
  { code: 200, label: "Finalized" },
  { code: 300, label: "Scheduled" },
  { code: 400, label: "In Progress" },
  { code: 500, label: "Completed" },
  { code: 600, label: "Cancelled" },
];

const REMOVAL_STATUS_CODES: Array<{ code: number; label: string }> = [
  { code: 100, label: "Draft" },
  { code: 200, label: "Finalized" },
  { code: 300, label: "Scheduled" },
  { code: 400, label: "In Progress" },
  { code: 500, label: "Completed" },
  { code: 600, label: "Closed" },
  { code: 700, label: "Cancelled" },
  { code: 800, label: "Lost" },
];

const buildStatusRules = (
  type: "SALES" | "INSTALLATION" | "REMOVAL",
  statuses: Array<{ code: number; label: string }>
): TriggerRule[] => {
  return statuses.map((status) => ({
    id: `${type}_STATUS_UPDATED_${status.code}`,
    label: `${type.charAt(0) + type.slice(1).toLowerCase()} status changed (${status.code} - ${status.label})`,
    category: type,
    sendEmail:
      type === "SALES" && (status.code === 301 || status.code === 302 || status.code === 400),
    audience:
      type === "SALES" && (status.code === 301 || status.code === 302 || status.code === 400)
        ? ["ENDUSER", "SIGNAGE", "SIGNAGE_GROUP"]
        : [],
    note:
      type === "SALES" && (status.code === 301 || status.code === 302 || status.code === 400)
        ? `Email enabled by policy for sales status code ${status.code}.`
        : `No notification required by policy for status code ${status.code}.`,
  }));
};

const triggerRules: TriggerRule[] = [
  {
    id: "ORDER_CREATED",
    label: "Order created",
    category: "SALES",
    sendEmail: true,
    audience: ["ENDUSER"],
    note: "Initial confirmation to end user.",
  },
  {
    id: "ORDER_FINALIZED",
    label: "Order finalized",
    category: "SALES",
    sendEmail: true,
    audience: ["ENDUSER", "SIGNAGE", "SIGNAGE_GROUP"],
    note: "Final order goes to end user and signage contacts.",
  },
  {
    id: "ORDER_EDITED",
    label: "Order edited",
    category: "SALES",
    sendEmail: true,
    audience: ["ENDUSER", "BROKERAGE", "SIGNAGE", "BROKERAGE_GROUP", "SIGNAGE_GROUP"],
    note: "Edited order update is sent to all mapped audiences.",
  },
  ...buildStatusRules("SALES", SALES_STATUS_CODES),
  ...buildStatusRules("INSTALLATION", INSTALLATION_STATUS_CODES),
  ...buildStatusRules("REMOVAL", REMOVAL_STATUS_CODES),
  {
    id: "SHIPPING_UPDATED",
    label: "Shipping status updated",
    category: "SHIPPING",
    sendEmail: false,
    audience: [],
    note: "Silent trigger. Shipping update should not send email.",
  },
  {
    id: "PAYMENT_RECEIVED",
    label: "Payment received",
    category: "PAYMENT",
    sendEmail: true,
    audience: ["ENDUSER", "BROKERAGE", "BROKERAGE_GROUP"],
    note: "Notify end user and brokerage contacts.",
  },
];

const categoryOptions = [
  "ALL",
  "SALES",
  "INSTALLATION",
  "REMOVAL",
  "SHIPPING",
  "PAYMENT",
] as const;

const policyOptions = [
  { value: "ALL", label: "All Policies" },
  { value: "SEND_EMAIL", label: "Send Email" },
  { value: "SILENT", label: "Silent (No Email)" },
];

const audienceOptions: Array<{ value: Audience | "ALL"; label: string }> = [
  { value: "ALL", label: "All Audience" },
  { value: "ENDUSER", label: "End User" },
  { value: "BROKERAGE", label: "Brokerage" },
  { value: "SIGNAGE", label: "Signage" },
  { value: "BROKERAGE_GROUP", label: "Brokerage Group" },
  { value: "SIGNAGE_GROUP", label: "Signage Group" },
];

const triggerTypeOptions = [
  { value: "ALL", label: "All Trigger Types" },
  { value: "STATUS", label: "Status Updates" },
  { value: "SYSTEM", label: "System Triggers" },
];

const categorySelectOptions = categoryOptions.map((item) => ({
  value: item,
  label: item,
}));

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: "38px",
    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 99999,
  }),
};

const getPolicyBadgeStyle = (sendEmail: boolean): React.CSSProperties =>
  sendEmail
    ? {
        backgroundColor: "#d2f5d2",
        color: "#2a6b2a",
        border: "1px solid #99d39c",
        borderRadius: "8px",
        padding: "3px 10px",
        fontSize: "12px",
      }
    : {
        backgroundColor: "#e1ebf8",
        color: "#276ada",
        border: "1px solid #a8c6ef",
        borderRadius: "8px",
        padding: "3px 10px",
        fontSize: "12px",
      };

const audienceBadgeStyle: React.CSSProperties = {
  backgroundColor: "#e8f5ff",
  color: "#1d5ea8",
  border: "1px solid #b8d8f4",
  borderRadius: "8px",
  padding: "3px 8px",
  fontSize: "11px",
};

const EmailTriggersDashboard: React.FC = () => {
  const [category, setCategory] = useState<(typeof categoryOptions)[number]>("ALL");
  const [showOnlyEmailEnabled, setShowOnlyEmailEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(policyOptions[0]);
  const [selectedAudience, setSelectedAudience] = useState(audienceOptions[0]);
  const [selectedTriggerType, setSelectedTriggerType] = useState(triggerTypeOptions[0]);
  const [selectedCategory, setSelectedCategory] = useState(categorySelectOptions[0]);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const filteredRules = useMemo(() => {
    return triggerRules.filter((rule) => {
      const categoryMatches = selectedCategory.value === "ALL" || rule.category === selectedCategory.value;
      const emailMatches = !showOnlyEmailEnabled || rule.sendEmail;
      const query = searchQuery.trim().toLowerCase();
      const searchMatches =
        !query ||
        rule.label.toLowerCase().includes(query) ||
        rule.id.toLowerCase().includes(query) ||
        rule.category.toLowerCase().includes(query) ||
        rule.note.toLowerCase().includes(query);
      const policyMatches =
        selectedPolicy.value === "ALL" ||
        (selectedPolicy.value === "SEND_EMAIL" && rule.sendEmail) ||
        (selectedPolicy.value === "SILENT" && !rule.sendEmail);
      const audienceMatches =
        selectedAudience.value === "ALL" || rule.audience.includes(selectedAudience.value);
      const triggerTypeMatches =
        selectedTriggerType.value === "ALL" ||
        (selectedTriggerType.value === "STATUS" && rule.id.includes("STATUS_UPDATED")) ||
        (selectedTriggerType.value === "SYSTEM" && !rule.id.includes("STATUS_UPDATED"));
      return (
        categoryMatches &&
        emailMatches &&
        searchMatches &&
        policyMatches &&
        audienceMatches &&
        triggerTypeMatches
      );
    });
  }, [
    category,
    showOnlyEmailEnabled,
    searchQuery,
    selectedCategory,
    selectedPolicy,
    selectedAudience,
    selectedTriggerType,
  ]);

  const silentCount = triggerRules.filter((rule) => !rule.sendEmail).length;
  const activeCount = triggerRules.length - silentCount;
  const summaryCards = [
    {
      title: "Total Triggers",
      value: triggerRules.length,
      icon: "ri-stack-line",
      bg: "#dbeafe",
      color: "#155dfc",
    },
    {
      title: "Email Enabled",
      value: activeCount,
      icon: "ri-mail-check-line",
      bg: "#dcfce7",
      color: "#00a63e",
    },
    {
      title: "Silent Triggers",
      value: silentCount,
      icon: "ri-mail-close-line",
      bg: "#fee2e2",
      color: "#dc2626",
    },
    {
      title: "Visible Records",
      value: filteredRules.length,
      icon: "ri-list-check-3",
      bg: "#e0e7ff",
      color: "#4f39f6",
    },
  ];
  const pageCount = Math.max(1, Math.ceil(filteredRules.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const paginatedRules = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredRules.slice(start, start + pageSize);
  }, [filteredRules, currentPage, pageSize]);

  const goToNextPage = () => setPageIndex((prev) => Math.min(prev + 1, pageCount - 1));
  const goToPreviousPage = () => setPageIndex((prev) => Math.max(prev - 1, 0));

  return (
    <div className="content">
      <div className="row g-3 mb-4">
        {summaryCards.map((card) => (
          <div key={card.title} className="col-12 col-sm-6 col-md-3 d-flex">
            <div
              className="card shadow-border-1 box-shadow-datatables bg-white w-100 h-100"
              style={{ borderRadius: "12px", border: "1px solid #E5E7EB" }}
            >
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1 text-gray-600 text-xs" style={{ fontSize: "0.82rem" }}>
                    {card.title}
                  </h6>
                  <h4 className="fw-bold mb-0 text-dark">{card.value}</h4>
                </div>
                <div
                  className="d-flex align-items-center justify-content-center rounded-4"
                  style={{
                    backgroundColor: card.bg,
                    color: card.color,
                    width: "34px",
                    height: "34px",
                    fontSize: "15px",
                  }}
                >
                  <i className={card.icon} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Email Trigger Policy Center</h4>
            <p className="mb-0 text-muted">
              Define which status updates send emails and who receives them.
            </p>
          </div>
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <form className="input-group po-search" autoComplete="off" style={{ minWidth: 320 }}>
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                name="email_trigger_search"
                className="form-control"
                placeholder="Search by trigger, id, category..."
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setPageIndex(0);
                }}
                autoComplete="off"
              />
            </form>
            <button
              type="button"
              className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="email-trigger-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <button
              type="button"
              className={`btn ${showOnlyEmailEnabled ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setShowOnlyEmailEnabled((prev) => !prev);
                setPageIndex(0);
              }}
            >
              {showOnlyEmailEnabled ? "Showing: Email only" : "Show email-enabled only"}
            </button>
          </div>
        </div>
        <div id="email-trigger-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100">
              <div className="col-md-3">
                <label className="form-label mb-1">Category</label>
                <Select
                  classNamePrefix="rs"
                  value={selectedCategory}
                  options={categorySelectOptions}
                  onChange={(option) => {
                    if (option) {
                      setSelectedCategory(option);
                      setCategory(option.value as (typeof categoryOptions)[number]);
                      setPageIndex(0);
                    }
                  }}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">Email Policy</label>
                <Select
                  classNamePrefix="rs"
                  value={selectedPolicy}
                  options={policyOptions}
                  onChange={(option) => {
                    if (option) {
                      setSelectedPolicy(option);
                      setPageIndex(0);
                    }
                  }}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">Audience</label>
                <Select
                  classNamePrefix="rs"
                  value={selectedAudience}
                  options={audienceOptions}
                  onChange={(option) => {
                    if (option) {
                      setSelectedAudience(option);
                      setPageIndex(0);
                    }
                  }}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">Trigger Type</label>
                <Select
                  classNamePrefix="rs"
                  value={selectedTriggerType}
                  options={triggerTypeOptions}
                  onChange={(option) => {
                    if (option) {
                      setSelectedTriggerType(option);
                      setPageIndex(0);
                    }
                  }}
                  styles={selectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-12 d-flex justify-content-md-end gap-2">
                <button type="button" className="btn btn-primary" onClick={() => setPageIndex(0)}>
                  Apply Filter
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setSelectedPolicy(policyOptions[0]);
                    setSelectedAudience(audienceOptions[0]);
                    setSelectedTriggerType(triggerTypeOptions[0]);
                    setSelectedCategory(categorySelectOptions[0]);
                    setShowOnlyEmailEnabled(false);
                    setCategory("ALL");
                    setSearchQuery("");
                    setPageIndex(0);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Trigger Rules</h5>
          <div className="d-flex align-items-center gap-2">
            <label className="mb-0 text-muted small">Records</label>
            <select
              className="form-select form-select-sm"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPageIndex(0);
              }}
              style={{ width: 80 }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="rdt-desktop shadow-sm">
          <div
            className="table-responsive production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <thead>
                <tr>
                  <th><div className="th-text">Trigger</div></th>
                  <th><div className="th-text">Category</div></th>
                  <th><div className="th-text">Email Policy</div></th>
                  <th><div className="th-text">Audience</div></th>
                  <th><div className="th-text">Notes</div></th>
                </tr>
              </thead>
              <tbody>
                {paginatedRules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <div className="text-sm fw-semibold">{rule.label}</div>
                      <small className="text-muted">{rule.id}</small>
                    </td>
                    <td><div className="text-sm">{rule.category}</div></td>
                    <td>
                      <span style={getPolicyBadgeStyle(rule.sendEmail)}>
                        {rule.sendEmail ? "Send email" : "Silent (no email)"}
                      </span>
                    </td>
                    <td>
                      {rule.audience.length ? (
                        <div className="d-flex flex-wrap gap-1">
                          {rule.audience.map((item) => (
                            <span key={item} style={audienceBadgeStyle}>
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">None</span>
                      )}
                    </td>
                    <td><div className="text-sm">{rule.note}</div></td>
                  </tr>
                ))}
                {!paginatedRules.length && (
                  <tr>
                    <td colSpan={5} className="text-center my-4 text-muted">
                      No triggers found for the selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-4 mb-4">
        <div className="po-pagination py-4 shadow-border-datatables">
          <div className="po-range">
            Showing {paginatedRules.length ? currentPage * pageSize + 1 : 0} to{" "}
            {currentPage * pageSize + paginatedRules.length} of {filteredRules.length} entries
          </div>

          <div className="po-actions">
            <div className="rows-group">
              <span className="po-rows-label">Rows per page:</span>
              <select
                className="form-select po-page-size"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPageIndex(0);
                }}
              >
                {[5, 10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <nav className="po-nav">
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 0 ? "disabled" : ""} me-3`}>
                  <button
                    type="button"
                    className="page-link d-flex align-items-center gap-1"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 0}
                    aria-label="Previous"
                  >
                    <i className="bi bi-chevron-left" />
                    <span className="d-none d-sm-inline preview-buttons">Previous</span>
                  </button>
                </li>

                <li className="page-item active">
                  <button type="button" className="page-link pagination-button" disabled>
                    {currentPage + 1}
                  </button>
                </li>

                <li className={`page-item ${currentPage >= pageCount - 1 ? "disabled" : ""} ms-3`}>
                  <button
                    type="button"
                    className="page-link d-flex align-items-center gap-1"
                    onClick={goToNextPage}
                    disabled={currentPage >= pageCount - 1}
                    aria-label="Next"
                  >
                    <span className="d-none d-sm-inline preview-buttons">Next</span>
                    <i className="bi bi-chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTriggersDashboard;

